import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Container, 
  Paper, 
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import Dashboard from '../components/manageAccount/Dashboard';
import OrganizationSettings from '../components/manageAccount/OrganizationSettings';
import TeamMembers from '../components/manageAccount/TeamMembers';
import Licenses from '../components/manageAccount/Licenses';
import Invoices from '../components/manageAccount/Invoices';
import Subscription from '../components/manageAccount/Subscription';
import { useSearchParams } from 'react-router-dom';

const MANAGE_ACCOUNT_TAB_KEYS = [
  'dashboard',
  'organization',
  'team',
  'licenses',
  'subscription',
  'invoices',
] as const;

function manageAccountTabIndexFromParam(tabParam: string | null): number {
  switch (tabParam) {
    case 'team':
      return 2;
    case 'licenses':
      return 3;
    case 'subscription':
      return 4;
    case 'invoices':
      return 5;
    case 'organization':
      return 1;
    case 'dashboard':
    default:
      return 0;
  }
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  mounted: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, mounted, ...other } = props;

  if (!mounted) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`manage-account-tabpanel-${index}`}
      aria-labelledby={`manage-account-tab-${index}`}
      {...other}
    >
      <Box
        sx={{
          py: { xs: 2, sm: 3, md: 4 },
          px: 0,
          backgroundColor: '#ffffff',
          background: '#ffffff',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        {children}
      </Box>
    </div>
  );
}

const ManageAccount: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  const initialTab = manageAccountTabIndexFromParam(tabParam);
  const [value, setValue] = useState<number>(initialTab);
  const [mountedTabs, setMountedTabs] = useState<Set<number>>(() => new Set([initialTab]));

  // Preload Dashboard, Organization, Subscription, and Billing after first paint.
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      setMountedTabs((prev) => {
        const next = new Set(prev);
        next.add(0);
        next.add(1);
        next.add(4);
        next.add(5);
        return next;
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  const markTabMounted = (tabIndex: number) => {
    setMountedTabs((prev) => {
      if (prev.has(tabIndex)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(tabIndex);
      return next;
    });
  };

  // Sync tab with URL when tab param is present (e.g. from payment success → subscription)
  useEffect(() => {
    const tabIndex = manageAccountTabIndexFromParam(tabParam);
    setValue(tabIndex);
    markTabMounted(tabIndex);
  }, [tabParam]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    markTabMounted(newValue);
    const tabKey = MANAGE_ACCOUNT_TAB_KEYS[newValue];
    const next = new URLSearchParams(searchParams);
    if (tabKey === 'dashboard') {
      next.delete('tab');
    } else {
      next.set('tab', tabKey);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <Box
      sx={{
        width: 'calc(100% + 48px)',
        minHeight: '100%',
        backgroundColor: '#ffffff',
        background: '#ffffff',
        marginLeft: { xs: '-24px', sm: '-24px', md: '-24px' },
        marginRight: { xs: '-24px', sm: '-24px', md: '-24px' },
        padding: { xs: '24px 24px', sm: '24px 32px', md: '32px 48px' },
        py: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box 
        sx={{ 
          width: '100%',
          mx: 'auto'
        }}
      >
      <Box 
        sx={{ 
          mb: { xs: 3, sm: 4 },
          textAlign: { xs: 'left', sm: 'left' }
        }}
      >
        <Typography 
          variant="h4" 
          component="h1"
          sx={{ 
            fontWeight: 600,
            mb: 1,
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' }
          }}
        >
          Manage Account
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          Manage your team, subscription, and account settings
        </Typography>
      </Box>

      <Paper 
        elevation={2}
        sx={{ 
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          background: '#ffffff',
          margin: 0,
          padding: 0,
          boxSizing: 'border-box',
        }}
      >
        <Box 
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="manage account tabs"
            selectionFollowsFocus
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile={isMobile}
            TabIndicatorProps={{ sx: { display: 'none' } }}
            sx={{
              minHeight: { xs: 48, sm: 64 },
              '& .MuiTab-root': {
                position: 'relative',
                overflow: 'visible',
                minHeight: { xs: 48, sm: 64 },
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                textTransform: 'none',
                fontWeight: 500,
                px: { xs: 1.5, sm: 2, md: 3 },
                '&.Mui-selected': {
                  fontWeight: 600,
                },
                '&.Mui-selected::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '72%',
                  maxWidth: '100%',
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  backgroundColor: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab label="Dashboard" />
            <Tab label="Organization" />
            <Tab label="Team Members" />
            <Tab label="Licenses" />
            <Tab label="Subscription" />
            <Tab label="Billing & Invoices" />
          </Tabs>
        </Box>

        <TabPanel value={value} index={0} mounted={mountedTabs.has(0)}>
          <Dashboard />
        </TabPanel>
        <TabPanel value={value} index={1} mounted={mountedTabs.has(1)}>
          <OrganizationSettings />
        </TabPanel>
        <TabPanel value={value} index={2} mounted={mountedTabs.has(2)}>
          <TeamMembers />
        </TabPanel>
        <TabPanel value={value} index={3} mounted={mountedTabs.has(3)}>
          <Licenses />
        </TabPanel>
        <TabPanel value={value} index={4} mounted={mountedTabs.has(4)}>
          <Subscription />
        </TabPanel>
        <TabPanel value={value} index={5} mounted={mountedTabs.has(5)}>
          <Invoices />
        </TabPanel>
      </Paper>
    </Box>
    </Box>
  );
};

export default ManageAccount;

